import { useState, useEffect, useMemo, useCallback } from 'react';
import { Spin, Typography, Tooltip, Tag, Button, Card, Space } from 'antd';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import { CloseOutlined, EditOutlined } from '@ant-design/icons';
import '../Entities/Entity.css';
import './ConnectionPage.css';
import { EntityImage } from '../Entities/EntityImage';
import ConnectionEditModal from '../Entities/ConnectionEditModal';
import PinToSidebarButton from '../Sidebar/PinToSidebarButton';
import { windowLabel } from '../Home/BrowseCalendar';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';
import { useSuperAdmins } from '../../hooks/use_super_admins';
import { useNodeInfo } from '../../hooks/use_node_info';
import { connectionState, edgeKind, edgeLabel, edgeViewpoint, isGroup, stateOf } from '../../utils/connections';

// Where an end opens: an app instance has its own page.
const entityPath = entity => (entity.entity_type === 'app' ? `/app/${entity.id}` : `/entity/${entity.id}`);

// One end of the connection: its picture over its name, which opens it.
function End({ entity, onOpen }) {
    return (
        <div className="connection-end">
            <EntityImage entity={entity} size={56} />
            <Typography.Link className="connection-end-name" onClick={() => onOpen(entity)}>
                {entity.name}
            </Typography.Link>
            <span className="connection-end-type">{entity.entity_type}</span>
        </div>
    );
}

/**
 * One connection's own page (/connection/:connectionId) — the entity page's
 * counterpart for an edge: both ends, with the person first the way a
 * membership is said; the role, the state and the window; who made it and
 * when. An admin of either end (your own person entity counts) or a
 * superadmin edits it in the same modal an entity's connected list uses,
 * looked at from the group's end, whose admins accept requests. The pin
 * button keeps it in the caller's sidebar.
 *
 * Read off GET /api/connections/:id (no state on it — worked out here the
 * way services/bonds.py does, utils/connections.js) plus the two ends and
 * the creator from GET /api/entities/:id. Closing lands on /connections.
 */
function ConnectionPage() {
    const api = useApi();
    const { connectionId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { isServerAdmin } = useSuperAdmins();
    const { featureEntityTypeIdentifiers } = useNodeInfo();

    const [conn, setConn] = useState(null);
    const [ends, setEnds] = useState(null); // { a, b, creator }
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    // Admin of each end, once asked; a superadmin needs no asking.
    const [adminOfEnd, setAdminOfEnd] = useState({});
    const [editing, setEditing] = useState(false);

    // The connection and its ends the first time; after an edit only the
    // connection is read again — its ends never change.
    const load = useCallback(async ({ endsToo }) => {
        const c = await api.get(`/api/connections/${connectionId}`);
        if (endsToo) {
            const entity = id => api.get(`/api/entities/${id}`, { cache: true });
            const [a, b, creator] = await Promise.all([
                entity(c.entity_a_id),
                entity(c.entity_b_id),
                c.created_by != null ? entity(c.created_by).catch(() => null) : Promise.resolve(null),
            ]);
            setEnds({ a, b, creator });
        }
        setConn(c);
    }, [api, connectionId]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setFailed(false);
        setConn(null);
        setEnds(null);
        load({ endsToo: true })
            .catch(() => { if (alive) setFailed(true); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [load]);

    const endAId = ends?.a.id;
    const endBId = ends?.b.id;
    useEffect(() => {
        if (endAId == null || endBId == null || !user?.id) return undefined;
        let alive = true;
        const adminOf = id => api.get(`/api/entities/${id}/admin-check`).then(d => !!d?.is_admin).catch(() => false);
        Promise.all([adminOf(endAId), adminOf(endBId)]).then(([a, b]) => {
            if (alive) setAdminOfEnd({ [endAId]: a, [endBId]: b });
        });
        return () => { alive = false; };
    }, [api, user?.id, endAId, endBId]);

    const edge = useMemo(() => (conn && ends ? {
        ...conn,
        a: ends.a,
        b: ends.b,
        creator: ends.creator,
        state: connectionState(conn),
        kind: edgeKind(ends.a, ends.b),
    } : null), [conn, ends]);

    const refresh = () => load({ endsToo: false }).catch(() => {});
    const openEntity = entity => navigate(entityPath(entity));

    if (loading) return <div className="entity-stage"><Spin size="large" style={{ marginTop: '22vh' }} /></div>;
    if (failed || !edge) {
        return (
            <div className="entity-stage">
                <Typography.Text style={{ marginTop: '22vh' }}>Connection not found</Typography.Text>
            </div>
        );
    }

    const [viewpoint, other] = edgeViewpoint(edge);
    const { first, second } = edgeLabel(edge);
    const canEdit = isServerAdmin || !!adminOfEnd[edge.a.id] || !!adminOfEnd[edge.b.id];
    const canAccept = isGroup(viewpoint) && !!adminOfEnd[viewpoint.id];
    const state = stateOf(edge.state);
    const pausedUntil = edge.state === 'paused' && edge.paused_until
        ? `Until ${dayjs(edge.paused_until).format('D MMM YYYY')}`
        : null;
    const empty = <Typography.Text type="secondary">—</Typography.Text>;

    const fields = [
        {
            key: 'when',
            label: 'When',
            children: edge.starts_at || edge.ends_at ? <Typography.Text>{windowLabel(edge)}</Typography.Text> : empty,
        },
        pausedUntil && {
            key: 'paused',
            label: 'Paused',
            children: <Typography.Text>{pausedUntil}</Typography.Text>,
        },
        {
            key: 'created',
            label: 'Created',
            children: edge.created_at ? <Typography.Text>{dayjs(edge.created_at).format('D MMM YYYY')}</Typography.Text> : empty,
        },
        {
            key: 'created_by',
            label: 'Created by',
            children: edge.creator ? (
                <Space size={8}>
                    <EntityImage entity={edge.creator} size={20} />
                    <Typography.Link onClick={() => openEntity(edge.creator)}>{edge.creator.name}</Typography.Link>
                </Space>
            ) : empty,
        },
    ].filter(Boolean);

    return (
        <div className="entity-stage">
            <div className="entity-page connection-page">
                {/* Corner icon actions — pinned to the viewport, see Entity.css */}
                <div className="entity-corner-actions">
                    <PinToSidebarButton connectionId={edge.id} />
                    <div className="entity-actions-sep" />
                    <Tooltip title="Close">
                        <Button
                            type="text"
                            className="entity-icon-btn entity-icon-btn--close"
                            aria-label="Close"
                            icon={<CloseOutlined />}
                            onClick={() => navigate('/connections')}
                        />
                    </Tooltip>
                </div>

                <Card className="glass-panel entity-card" styles={{ body: { padding: '28px 28px 24px' } }}>
                    <div className="entity-card-head">
                        <div className="connection-ends">
                            <End entity={first} onOpen={openEntity} />
                            <span className="connection-ends-link" aria-hidden="true">↔</span>
                            <End entity={second} onOpen={openEntity} />
                        </div>
                        <div className="entity-meta-row">
                            {edge.role && <Tag style={{ textTransform: 'capitalize' }}>{edge.role}</Tag>}
                            <Tooltip title={pausedUntil}><Tag color={state.color}>{state.label}</Tag></Tooltip>
                            {featureEntityTypeIdentifiers && <Tag>{edge.kind}</Tag>}
                        </div>
                    </div>

                    {fields.map(item => (
                        <div className="entity-field" key={item.key}>
                            <div className="entity-field-label t-label-upper">{item.label}</div>
                            <div className="entity-field-value">{item.children}</div>
                        </div>
                    ))}
                </Card>

                {canEdit && (
                    <div className="entity-toolbar">
                        <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>
                            Edit connection
                        </Button>
                    </div>
                )}

                <ConnectionEditModal
                    open={editing}
                    onClose={() => setEditing(false)}
                    conn={{ ...edge, connection_id: edge.id, entity: other }}
                    currentEntityId={viewpoint.id}
                    isAdmin={canEdit}
                    canAcceptConnection={canAccept}
                    userId={user?.id}
                    onSuccess={() => { setEditing(false); refresh(); }}
                    showOpenConnection={false}
                />
            </div>
        </div>
    );
}

export default ConnectionPage;
